import { User, UserRole } from '../types';
import { canSeeConfig } from '../constants/constants';

/**
 * Multi-role helpers (UI) — mirror of svp-ts-api/src/modules/user/roles.util.ts.
 *
 * Source of truth is `user.roles`. The legacy scalar `role` / `isOp` / `isAdmin` fields were dropped in
 * Phase 5 (migration M3), so `roles` is now the ONLY identity input. There is no "primary role" —
 * `firstRole` exists ONLY to choose a landing page / menu order (see §2.4).
 */
export type RoleCarrier = Pick<Partial<User>, 'roles'>;

/**
 * Role hierarchy (most → least important). Drives (1) sidebar group order and (2) the post-login landing
 * for multi-role users. `OP_MANAGER` outranks `OPERATING_PARTNER`; `ADMIN` is last (so `X + Admin` keeps
 * X's landing, while an admin-only user still lands on the admin page as their only role).
 */
export const ROLE_ORDER: readonly UserRole[] = [
  UserRole.ACCOUNTANT,
  UserRole.OP_MANAGER,
  UserRole.DEAL_OWNER,
  UserRole.PROXY,
  UserRole.OPERATING_PARTNER,
  UserRole.ADMIN,
];

export function rolesOf(user: RoleCarrier | null | undefined): UserRole[] {
  if (!user || !Array.isArray(user.roles)) {
    return [];
  }
  return user.roles;
}

export function hasRole(user: RoleCarrier | null | undefined, role: UserRole): boolean {
  return rolesOf(user).includes(role);
}

export function isSubmitter(user: RoleCarrier | null | undefined): boolean {
  return hasRole(user, UserRole.OPERATING_PARTNER);
}

export function isManager(user: RoleCarrier | null | undefined): boolean {
  return hasRole(user, UserRole.OP_MANAGER);
}

export function isAdmin(user: RoleCarrier | null | undefined): boolean {
  return hasRole(user, UserRole.ADMIN);
}

/** Most important role by ROLE_ORDER (landing/menu ordering only — NOT a stored "primary"). */
export function firstRole(user: RoleCarrier | null | undefined): UserRole | undefined {
  const roles = rolesOf(user);
  return ROLE_ORDER.find((role) => roles.includes(role));
}

/**
 * Union of the page sets over the user's held roles. Replaces the old single-role `pages` / `adminPages` /
 * `OPPages` / `OPAdminPages` lookups: e.g. {OP_MANAGER, OPERATING_PARTNER} yields exactly the old OPPages,
 * and {ACCOUNTANT, ADMIN} yields exactly the old accountant adminPages. Used for route guarding.
 */
export function allowedPages(user: RoleCarrier | null | undefined): string[] {
  const pages = new Set<string>();
  for (const role of rolesOf(user)) {
    const config = canSeeConfig[role as keyof typeof canSeeConfig];
    config?.pages.forEach((page) => pages.add(page));
  }
  return Array.from(pages);
}
